import { AgColDefsI, AgGridI } from '../../shared/common-components/ag-grid/ag-grid.model';
import { CustomHeaderComponent } from '../../shared/common-components/ag-grid/custom-header/custom-header.component';
import { TextRendererComponent } from '../../shared/common-components/ag-grid/text-renderer/text-renderer.component';
import { DateColWidth, NameColWidth, StatusColWidth } from '../../shared/enum/common-enum';
import { paginationModel } from '../../shared/models/common.model';
import { SupportTicketActivityRendererComponent } from './support-ticket-activity-renderer.component';

export type SupportTicketStatusFilter = 'All' | 'Open' | 'TicketAnswered' | 'Closed' | null;

export interface SupportTicketAttachment {
  fileName?: string;
  mimeType?: string;
  base64Resource?: string;
  url?: string;
}

export interface SupportTicketListItem {
  ticketId?: string;
  title?: string;
  subscriptionId?: string;
  status?: string;
  createdAt?: string | Date;
  lastModifiedAt?: string | Date;
  companyId?: number;
  companyName?: string;
  ipAddress?: string;
  createdByUserName?: string;
  hasUnreadCustomerReply?: boolean;
  statusDisplay?: string;
  activityDisplay?: string;
  createdAtDisplay?: string;
  lastModifiedAtDisplay?: string;
  subscriptionDisplay?: string;
  companyDisplay?: string;
}

export interface SupportTicketMessage {
  id?: string;
  senderName?: string;
  senderRole?: string;
  bodyHtmlOrText?: string;
  createdAt?: string | Date;
  attachments?: SupportTicketAttachment[];
}

export interface SupportTicketListResponse {
  items?: SupportTicketListItem[];
  totalCount?: number;
  openCount?: number;
  answeredCount?: number;
  closedCount?: number;
  unreadCount?: number;
}

export interface SupportTicketDetail {
  ticketId?: string;
  title?: string;
  category?: string;
  subscriptionId?: string;
  status?: string;
  createdAt?: string | Date;
  lastModifiedAt?: string | Date;
  companyId?: number;
  companyName?: string;
  ipAddress?: string;
  createdByUserName?: string;
  hasUnreadCustomerReply?: boolean;
  messages?: SupportTicketMessage[];
}

export class GetAllSupportTicketsRequest {
  companyId = 0;
  ipAddress: string | null = null;
  searchText: string | null = null;
  status: SupportTicketStatusFilter = 'All';
  page = 1;
  pageSize = 10;
}

export class GetSupportTicketByIdRequest {
  ticketId: string | null = null;
  companyId = 0;
  ipAddress: string | null = null;
  markAsRead = false;
}

export class ReplySupportTicketRequest {
  ticketId: string | null = null;
  content: string | null = null;
  companyId = 0;
  userId = 0;
  userName: string | null = null;
  ipAddress: string | null = null;
  isFromSupport = true;
  attachments: SupportTicketAttachment[] = [];
}

export class CloseSupportTicketRequest {
  ticketId: string | null = null;
  companyId = 0;
  ipAddress: string | null = null;
}

export class SupportTicketListFilter extends paginationModel {
  status: SupportTicketStatusFilter = 'All';
  companyId = 0;
}

const TicketIdColumn: AgColDefsI = {
  headerName: 'Ticket ID',
  field: 'ticketId',
  headerComponent: CustomHeaderComponent,
  minWidth: 150,
  maxWidth: 180,
  sortable: true,
  editable: false,
  headerClass: 'custom-column-group-cell',
  cellClass: 'custom-column-group-cell',
  cellRenderer: TextRendererComponent,
  cellRendererParams: { isCursor: true, textPrimary: true }
};

const TitleColumn: AgColDefsI = {
  headerName: 'Title',
  field: 'title',
  headerComponent: CustomHeaderComponent,
  minWidth: NameColWidth.minWidth,
  flex: 1,
  sortable: true,
  editable: false,
  headerClass: 'custom-column-group-cell',
  cellClass: 'custom-column-group-cell',
  cellRenderer: TextRendererComponent,
  cellRendererParams: { isCursor: true, textPrimary: true }
};

const CompanyColumn: AgColDefsI = {
  headerName: 'Client Name',
  field: 'companyDisplay',
  headerComponent: CustomHeaderComponent,
  minWidth: NameColWidth.minWidth,
  flex: 1,
  sortable: true,
  editable: false,
  headerClass: 'custom-column-group-cell',
  cellClass: 'custom-column-group-cell',
  cellRenderer: TextRendererComponent,
  cellRendererParams: { isCursor: false }
};

const SubscriptionColumn: AgColDefsI = {
  headerName: 'Subscription',
  field: 'subscriptionDisplay',
  headerComponent: CustomHeaderComponent,
  minWidth: 120,
  maxWidth: 160,
  sortable: true,
  editable: false,
  headerClass: 'custom-column-group-cell',
  cellClass: 'custom-column-group-cell',
  cellRenderer: TextRendererComponent,
  cellRendererParams: { isCursor: false }
};

const StatusColumn: AgColDefsI = {
  headerName: 'Status',
  field: 'statusDisplay',
  headerComponent: CustomHeaderComponent,
  minWidth: StatusColWidth.minWidth,
  maxWidth: StatusColWidth.maxWidth,
  sortable: true,
  editable: false,
  headerClass: 'custom-column-group-cell',
  cellClass: 'custom-column-group-cell',
  cellRenderer: TextRendererComponent,
  cellRendererParams: { isCursor: false }
};

const ActivityColumn: AgColDefsI = {
  headerName: 'Activity',
  field: 'activityDisplay',
  headerComponent: CustomHeaderComponent,
  minWidth: 140,
  maxWidth: 160,
  sortable: true,
  editable: false,
  headerClass: 'custom-column-group-cell',
  cellClass: 'custom-column-group-cell',
  cellRenderer: SupportTicketActivityRendererComponent
};

const CreatedColumn: AgColDefsI = {
  headerName: 'Created',
  field: 'createdAtDisplay',
  headerComponent: CustomHeaderComponent,
  minWidth: DateColWidth.minWidth,
  maxWidth: DateColWidth.maxWidth,
  sortable: true,
  editable: false,
  headerClass: 'custom-column-group-cell',
  cellClass: 'custom-column-group-cell',
  cellRenderer: TextRendererComponent,
  cellRendererParams: { isCursor: false }
};

const LastModifiedColumn: AgColDefsI = {
  headerName: 'Last Modified',
  field: 'lastModifiedAtDisplay',
  headerComponent: CustomHeaderComponent,
  minWidth: DateColWidth.minWidth,
  maxWidth: DateColWidth.maxWidth,
  sortable: true,
  editable: false,
  headerClass: 'custom-column-group-cell',
  cellClass: 'custom-column-group-cell',
  cellRenderer: TextRendererComponent,
  cellRendererParams: { isCursor: false }
};

export const SupportTicketColumns: AgColDefsI[] = [
  TicketIdColumn,
  TitleColumn,
  CompanyColumn,
  SubscriptionColumn,
  StatusColumn,
  ActivityColumn,
  CreatedColumn,
  LastModifiedColumn
];

export const AgGridDataModelSupportTickets: AgGridI = {
  colDefs: SupportTicketColumns,
  rowSelection: 'single',
  rowHeight: 40,
  showPagination: true
};
